import { subscribe } from 'valtio';

import { browserState } from '@modules/BrowserModule/state';
import { MicrophoneAccessType } from '@modules/BrowserModule/types';
import { phoneState } from '../state';
import { WebphoneConnectionStatus } from '../types';

/**
 * - `registered`: the webphone is connected.
 * - `unavailable`: no microphone, so the softphone never mounts.
 * - `user-gated`: the user must answer the microphone prompt, or take over a
 *   registration held by another tab or device.
 * - `timeout`: none of the above within `timeoutMs`.
 */
export type WebphoneRegistrationOutcome =
  | 'registered'
  | 'unavailable'
  | 'user-gated'
  | 'timeout';

interface WaitForWebphoneRegistrationOptions {
  timeoutMs: number;
  /** Called with each webphone connection status seen while waiting. */
  onStatusChange?: (status: WebphoneConnectionStatus) => void;
}

async function isMicrophonePromptPending(): Promise<boolean> {
  if (browserState.microphoneAccess !== MicrophoneAccessType.UNKNOWN) {
    return false;
  }
  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state === 'prompt';
  } catch {
    // Browsers without the microphone permission name: keep waiting.
    return false;
  }
}

function evaluate(): WebphoneRegistrationOutcome | null {
  if (phoneState.phoneConnected) {
    return 'registered';
  }
  if (
    phoneState.connectionStatus === WebphoneConnectionStatus.WaitingToConnect ||
    phoneState.connectionStatus ===
      WebphoneConnectionStatus.RegistrationOverwritten
  ) {
    return 'user-gated';
  }
  if (
    browserState.microphoneAccess === MicrophoneAccessType.PERMISSION_DENIED ||
    browserState.microphoneAccess === MicrophoneAccessType.ERROR
  ) {
    return 'unavailable';
  }
  // RegistrationFailed is not final: the webphone retries on its own.
  return null;
}

/**
 * Waits for the softphone MFE to register after sign-in, or for a reason it
 * won't. Resolves; never rejects.
 *
 * The microphone prompt is only checked at the deadline: on a first visit the
 * permission reads `prompt` before SoftPhone has asked for it, and the agent
 * may still grant it in time.
 */
function waitForWebphoneRegistration({
  timeoutMs,
  onStatusChange,
}: WaitForWebphoneRegistrationOptions): Promise<WebphoneRegistrationOutcome> {
  return new Promise((resolve) => {
    let lastStatus: WebphoneConnectionStatus | undefined;
    let done = false;
    const unsubscribers: Array<() => void> = [];
    const timer = setTimeout(() => {
      void isMicrophonePromptPending().then((pending) =>
        finish(pending ? 'user-gated' : 'timeout'),
      );
    }, timeoutMs);

    function finish(outcome: WebphoneRegistrationOutcome) {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      resolve(outcome);
    }

    function check() {
      if (phoneState.connectionStatus !== lastStatus) {
        lastStatus = phoneState.connectionStatus;
        onStatusChange?.(lastStatus);
      }
      const outcome = evaluate();
      if (outcome) {
        finish(outcome);
      }
    }

    unsubscribers.push(subscribe(phoneState, check, true));
    unsubscribers.push(subscribe(browserState, check, true));
    check();
  });
}

export default waitForWebphoneRegistration;
